import { useComputed } from "@preact/signals";
import {
  selectedComposerId,
  selectedOrgId,
  selectedCityId,
  selectedCitySource,
  radiusKm,
  selectedGenre,
  GENRE_OPTIONS,
  selectedPiece,
  selectedPieceKey,
  setPieceFilter,
  clearPieceFilter,
  setCityFilter,
  clearCityFilter,
  selectedCountry,
  countryOptions,
  setCountryFilter,
  focusCityAreaOnMap,
  focusCountryOnMap,
  radiusCenter,
  clearComposerFilter,
  clearOrgFilter,
  clearAllFilters,
  resetMapToWorld,
  activeFilterCount,
  filtersExpanded,
  showPossible,
  toggleShowPossible,
  showExtras,
  toggleShowExtras,
  timeFrom,
  timeTo,
  setTimeWindow,
  clearTimeFrom,
  clearTimeTo,
  historyBoundsMinYear,
  SLIDER_MAX_YEAR,
  composers,
  orgs,
  cities,
} from "../state/store";
import { searchComposers, searchCities, searchOrgs } from "../data/search-index";
import { ensureFacets } from "../data/loader";
import { fetchPieces } from "../data/api";
import { AutoComplete } from "./AutoComplete";
import { SourcesSelect } from "./SourcesSelect";
import { HistorySlider } from "./HistorySlider";
import { countryDisplayName } from "../utils/format";
import type { City, Composer, Org, Piece } from "../types";

/** ALL concert filtering lives here — one collapsible section between the header and the
 *  map. The always-visible row carries the essentials (composer, organization, sources,
 *  time); "More filters" reveals piece, city, country, genre and precise dates. Each active filter shows
 *  in (and clears from) its own field; "Clear filters" with its count badge is the one
 *  aggregate affordance, so nothing can be filtering invisibly. */
export function FilterPanel() {
  const composerId = selectedComposerId.value;
  const expanded = filtersExpanded.value;

  const composerValue = useComputed(() => {
    const id = selectedComposerId.value;
    if (id === null) return "";
    const c = composers.value.find((c) => c.id === id);
    return c ? c.name : "";
  });
  const composerName = composerValue.value;

  const orgValue = useComputed(() => {
    const id = selectedOrgId.value;
    if (id === null) return "";
    const o = orgs.value.find((o) => o.id === id);
    return o ? o.name : "";
  });

  const cityValue = useComputed(() => {
    const id = selectedCityId.value;
    if (id === null) return "";
    const c = cities.value.find((c) => c.id === id);
    return c ? cityLabel(c) : "";
  });

  // Pieces aren't in a local list, so the label trails the key by one round-trip on a
  // shared ?piece= link. Show the key's presence rather than an empty box in that gap.
  const pieceValue = useComputed(() => {
    const piece = selectedPiece.value;
    if (piece) return pieceText(piece);
    return selectedPieceKey.value ? "…" : "";
  });

  // Piece/city/country/genre live in the expanded area; count them so the collapsed
  // toggle can say when something hidden is active.
  const hiddenActive =
    (selectedPieceKey.value !== null ? 1 : 0) +
    (selectedCityId.value !== null ? 1 : 0) +
    (selectedCountry.value ? 1 : 0) +
    (selectedGenre.value ? 1 : 0);

  return (
    <section class="filter-panel" aria-label="Concert filters" id="filters">
      <div class="filter-grid">
        <div class="search-field">
          <AutoComplete<Composer>
            label="composer"
            placeholder="Composer"
            onSearch={async (q) => {
              await ensureFacets();
              return searchComposers(q);
            }}
            onSelect={(c) => {
              selectedComposerId.value = c.id;
              resetMapToWorld();
            }}
            renderItem={(c) => (
              <span class="ac-row">
                <span class="ac-row-main">{c.name}</span>
                <span class="ac-row-count">{c.count}</span>
              </span>
            )}
            itemLabel={(c) => c.name}
            value={composerValue.value}
            onClear={clearComposerFilter}
          />
        </div>

        <div class="search-field">
          <AutoComplete<Org>
            label="organization"
            placeholder="Organization"
            onSearch={async (q) => {
              await ensureFacets();
              return searchOrgs(q);
            }}
            onSelect={(o) => {
              selectedOrgId.value = o.id;
              resetMapToWorld();
            }}
            renderItem={(o) => (
              <span class="ac-row">
                <span class="ac-row-main">{o.name}</span>
                <span class="ac-row-count">{o.count}</span>
              </span>
            )}
            itemLabel={(o) => o.name}
            value={orgValue.value}
            onClear={clearOrgFilter}
          />
        </div>

        <div class="search-field filter-sources">
          <SourcesSelect />
        </div>

        {/* No field label: a year-range slider is self-describing, and the label row made
            this cell taller than its neighbors (ragged alignment, extra height on phones). */}
        <div class="search-field filter-time">
          <HistorySlider />
        </div>

        <div class="filter-actions">
          <button
            class="filter-more-btn"
            type="button"
            aria-expanded={expanded}
            aria-controls="filter-extra"
            onClick={() => {
              filtersExpanded.value = !expanded;
            }}
          >
            {expanded ? "Fewer filters" : "More filters"}
            {!expanded && hiddenActive > 0 && (
              <span class="filter-more-active">{hiddenActive}</span>
            )}
          </button>
          {/* The one place all filtering can be undone. The count badge keeps filters
              that are out of sight (piece/city/country/genre while collapsed) from
              filtering invisibly — the job the removed chips bar used to do. */}
          {activeFilterCount.value > 0 && (
            <button
              class="filter-clear-all"
              type="button"
              onClick={clearAllFilters}
              aria-label={`Clear ${activeFilterCount.value} active filters`}
            >
              Clear filters
              <span class="filter-more-active">{activeFilterCount.value}</span>
            </button>
          )}
        </div>
      </div>

      {selectedGenre.value && (
        <p class="genre-note">
          Genre is inferred from programs and presenters; concerts we haven’t classified
          yet are hidden while this filter is active. Historical archives carry no genre
          data and are excluded.
        </p>
      )}

      {expanded && (
        <div class="filter-grid filter-grid-extra" id="filter-extra">
          {/* Piece search is server-backed (~33k facets live in D1, not in the browser).
              `key` remounts the field when the composer scope changes, so a stale result
              list from the previous scope can't linger under the new placeholder. */}
          <div class="search-field">
            <AutoComplete<Piece>
              key={`piece-${composerId ?? "all"}`}
              label="piece"
              placeholder={
                composerName ? `Search ${lastName(composerName)}'s pieces...` : "Piece"
              }
              onSearch={(q) => fetchPieces(q, { composer: composerId })}
              onSelect={(p) => {
                setPieceFilter(p);
                resetMapToWorld();
              }}
              renderItem={(p) => (
                <span class="ac-row">
                  <span class="ac-row-main">
                    <span class="ac-piece-label">{p.label}</span>
                    {p.composer && !composerId && (
                      <span class="ac-piece-composer">{p.composer}</span>
                    )}
                  </span>
                  <span class="ac-row-count">{p.count}</span>
                </span>
              )}
              itemLabel={(p) => (composerId ? p.label : pieceText(p))}
              value={pieceValue.value}
              onClear={clearPieceFilter}
              openOnFocus
              debounceMs={180}
            />
          </div>

          <div class="search-field">
            <AutoComplete<City>
              label="city"
              placeholder="City"
              onSearch={async (q) => {
                await ensureFacets();
                return searchCities(q);
              }}
              onSelect={(c) => {
                setCityFilter(c.id, "manual");
                // Unlike composer/org/piece — which reset the camera to the whole globe
                // because their matches are scattered — a city filter has one obvious
                // place to be looking. Frame it (plus any active radius) rather than
                // leaving the reader zoomed into a region the filter just emptied.
                focusCityAreaOnMap(c, radiusKm.value);
              }}
              renderItem={(c) => (
                <span class="ac-row">
                  <span class="ac-row-main">{cityLabel(c)}</span>
                  <span class="ac-row-count">{c.count}</span>
                </span>
              )}
              itemLabel={cityLabel}
              value={cityValue.value}
              onClear={clearCityFilter}
            />
            {/* Radius search: widen the picked city to "within N km". Only offered for a
                manual pick — popup-sourced city filters are transient narrowing. The
                filter is on city centroids (concerts carry their city's coordinates),
                so treat the cutoff as approximate by nature. */}
            {selectedCityId.value !== null && selectedCitySource.value === "manual" && (
              <label class="radius-select">
                <span class="radius-select-label">within</span>
                <select
                  value={radiusKm.value ?? ""}
                  onChange={(e) => {
                    const v = (e.target as HTMLSelectElement).value;
                    const km = v ? parseInt(v, 10) : null;
                    radiusKm.value = km;
                    // Re-frame: widening to 250 km while the camera still holds one city
                    // shows none of the ground the filter just took in. radiusCenter is
                    // the picked city's record (null only before the city lists land).
                    const center = radiusCenter.value ?? cities.value.find(
                      (c) => c.id === selectedCityId.value,
                    );
                    if (center) focusCityAreaOnMap(center, km);
                  }}
                >
                  <option value="">this city only</option>
                  <option value="25">25 km</option>
                  <option value="50">50 km</option>
                  <option value="100">100 km</option>
                  <option value="250">250 km</option>
                </select>
              </label>
            )}
          </div>

          {/* Country. A native select rather than an autocomplete: ~110 options is small
              enough to scan, and on phones it opens the OS picker instead of a custom
              listbox. The filter itself is long-standing (URL-synced, applied server-side
              to the list and client-side to the markers) — until now it had no field and
              could only arrive via a shared link. */}
          <div class="search-field filter-country">
            <select
              class="genre-select"
              aria-label="Country"
              value={selectedCountry.value}
              onChange={(e) => {
                const code = (e.target as HTMLSelectElement).value;
                setCountryFilter(code);
                focusCountryOnMap(code);
              }}
            >
              <option value="">Country</option>
              {countryOptions.value.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Genre facet — a plain select over the six derived buckets. Lives in the
              expanded area (not the always-visible row) because classification is
              deliberately partial (works catalogue + program titles + presenter type)
              and it hides unclassified concerts while active — a power-user filter, not
              a headline one. The note under the grid says so while a genre is active. */}
          <div class="search-field filter-genre">
            <select
              class="genre-select"
              aria-label="Genre"
              value={selectedGenre.value}
              onChange={(e) => {
                selectedGenre.value = (e.target as HTMLSelectElement).value;
                if (selectedGenre.value) resetMapToWorld();
              }}
            >
              <option value="">Genre</option>
              {GENRE_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          {/* Precise date bounds. The two inputs cross-constrain (from.max = to, to.min =
              from) so the native picker opens in a sensible range instead of offering the
              1600s for an upper bound; erasing a field (committed on change, so partial
              typing doesn't wipe the filter) clears that edge, same as its "×". */}
          <div class="search-field search-dates">
            <label>
              <span class="field-label">
                From
                {timeFrom.value > `${historyBoundsMinYear.value}-01-01` && (
                  <DateClear label="Clear from date" onClear={clearTimeFrom} />
                )}
              </span>
              <input
                type="date"
                value={isoDateOnly(timeFrom.value)}
                min={`${historyBoundsMinYear.value}-01-01`}
                max={timeTo.value ? isoDateOnly(timeTo.value) : `${SLIDER_MAX_YEAR}-12-31`}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  // A from-date past the current to-date opens the end rather than
                  // producing an inverted window the API would answer with nothing.
                  if (v) setTimeWindow(v, timeTo.value && v > timeTo.value ? "" : timeTo.value);
                }}
                onChange={(e) => {
                  if (!(e.target as HTMLInputElement).value) clearTimeFrom();
                }}
              />
            </label>
            <label>
              <span class="field-label">
                To
                {Boolean(timeTo.value) && (
                  <DateClear label="Clear to date" onClear={clearTimeTo} />
                )}
              </span>
              <input
                type="date"
                value={isoDateOnly(timeTo.value)}
                min={isoDateOnly(timeFrom.value)}
                max={`${SLIDER_MAX_YEAR}-12-31`}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  if (v) setTimeWindow(timeFrom.value, v);
                }}
                onChange={(e) => {
                  if (!(e.target as HTMLInputElement).value) clearTimeTo();
                }}
              />
            </label>
          </div>

          {/* Certainty: the 'possible' tier — memoir events the source never dated to a day
              ("Winter 1812/13") or placed exactly. They appear inline in the list and on the
              map, badged 'possible'. On by default (memoir sources are mostly this tier);
              unticking restricts to confirmed+probable. */}
          <div class="search-field filter-certainty">
            <label class="certainty-toggle">
              <input
                type="checkbox"
                checked={showPossible.value}
                onChange={toggleShowPossible}
              />
              <span class="certainty-toggle-text">
                <span class="certainty-toggle-name">Uncertain events</span>
                <span class="certainty-toggle-detail">
                  include fuzzy-dated memoir events (badged “possible”)
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Always in view, unlike the certainty box above: it changes what the whole site is
          showing, so a reader must be able to find it and see at a glance that it is on. */}
      <div class="filter-extras">
        <label class="certainty-toggle">
          <input type="checkbox" checked={showExtras.value} onChange={toggleShowExtras} />
          <span class="certainty-toggle-text">
            <span class="certainty-toggle-name">Show non-classical and kids’ events</span>
            <span class="certainty-toggle-detail">
              {showExtras.value
                ? "Labelled on each card. We check these less carefully than concerts, and Explore still counts concerts only."
                : "Pop, jazz, musicals, ballet, theatre and talks from the same calendars."}
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

/** One-line text for a piece: "Beethoven: Symphony no. 5 in C minor, op. 67".
 *  Colon, not an em dash — the convention concert programs and record sleeves use. */
export function pieceText(piece: Piece): string {
  return piece.composer ? `${piece.composer}: ${piece.label}` : piece.label;
}

/** "Kraków, Poland" — one spelling of a city for the field, its suggestions and its
 *  filled-in value, so the three can't drift. Goes through countryDisplayName because a
 *  few dozen cities carry only the ISO code as their country name. */
function cityLabel(c: City): string {
  return `${c.name}, ${countryDisplayName(c.country_code, c.country_name)}`;
}

/** Surname for the scoped placeholder: "Search Beethoven's pieces...". */
function lastName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] || name;
}

/** <input type="date"> wants a bare YYYY-MM-DD (timeTo may be "" = open-ended). */
function isoDateOnly(v: string): string {
  return v.slice(0, 10);
}

/** The "×" on a date field's label row. preventDefault stops the wrapping <label> from
 *  forwarding the click into the date input (which would pop the picker mid-clear). */
function DateClear({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button
      type="button"
      class="date-clear"
      aria-label={label}
      onClick={(e) => {
        e.preventDefault();
        onClear();
      }}
    >
      &times;
    </button>
  );
}
